import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ShieldCheck, AlertTriangle, Check, X, ChevronDown } from "lucide-react";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Auto scope-completeness check — flags missing standard trades before the bid goes out.
export default function ScopeGapCard({ bidId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!bidId) return;
    api.get(`/bids/${bidId}/scope-gap`).then((r) => setData(r.data)).catch(() => {});
  }, [bidId]);
  if (!data) return null;
  const cov = data.coverage || {};
  const pct = cov.pct ?? 0;
  const tone = pct >= 90
    ? { c: "#10B981", bg: "#ECFDF5", t: "#047857", Icon: ShieldCheck }
    : pct >= 70
    ? { c: "#F59E0B", bg: "#FFFBEB", t: "#B45309", Icon: AlertTriangle }
    : { c: "#EF4444", bg: "#FEF2F2", t: "#B91C1C", Icon: AlertTriangle };
  const gaps = data.gaps || [];
  return (
    <Card className="p-5 mt-4" data-testid="scope-gap-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: tone.bg, color: tone.c }}>
          <tone.Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-800">Scope gap check</h3>
            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded-full" style={{ background: tone.bg, color: tone.t }} data-testid="scope-gap-coverage">{pct}% covered</span>
          </div>
          <div className="text-xs text-slate-500">{cov.present}/{cov.relevant} standard trades present · {cov.missing} gap{cov.missing === 1 ? "" : "s"}</div>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="scope-gap-toggle" className="text-xs text-[#003E87] hover:underline flex items-center gap-1 shrink-0">
          {open ? "Hide" : "Details"} <ChevronDown className={`w-3.5 h-3.5 transition ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: tone.c }} />
      </div>
      {gaps.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="scope-gap-list">
          {gaps.map((g) => (
            <span key={g.label} data-testid={`scope-gap-missing-${slug(g.label)}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
              <X className="w-3 h-3" /> {g.label}
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> All standard trades for this scope are represented.</div>
      )}
      {open && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-3" data-testid="scope-gap-details">
          {data.trades.map((tr) => (
            <div key={tr.trade}>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">{tr.trade}</div>
              <div className="flex flex-wrap gap-1.5">
                {tr.items.map((it) => (
                  <span key={it.label} data-testid={`scope-gap-item-${slug(it.label)}`}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${it.present ? "bg-emerald-50 text-emerald-700 border-emerald-200" : it.relevant ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                    {it.present ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {it.label}
                    {!it.relevant && <span className="text-slate-300">· n/a</span>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
